import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { KeyRound, Plug, RefreshCw, UserPlus, Search, Link2, AlertTriangle, CheckCircle2, Download, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, imgUrl } from "@/lib/api";
import { PageHeader, StatusBadge, DemoBadge, EmptyState, Pager } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AgentDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [assignOpen, setAssignOpen] = useState(false);
  const [prodSearch, setProdSearch] = useState("");
  const [prodPage, setProdPage] = useState(1);
  const [resetPw, setResetPw] = useState(null);

  useEffect(() => {
    const connected = params.get("connected");
    const oauthError = params.get("oauth_error");
    if (connected) {
      toast.success(`${connected.charAt(0).toUpperCase() + connected.slice(1)} seller account connected`);
      setParams({}, { replace: true });
    }
    if (oauthError) {
      toast.error(`Authorization failed for ${oauthError} — please try again`);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const { data: agent, refetch } = useQuery({
    queryKey: ["agent-detail", id],
    queryFn: async () => (await api.get(`/admin/agents/${id}`)).data,
  });
  const { data: assigned } = useQuery({
    queryKey: ["agent-products", id, prodPage, prodSearch],
    queryFn: async () => (await api.get(`/admin/agents/${id}/products?page=${prodPage}&limit=10&search=${encodeURIComponent(prodSearch)}`)).data,
  });
  const { data: catalog } = useQuery({
    queryKey: ["catalog-picker", prodSearch],
    queryFn: async () => (await api.get(`/admin/products?page=1&limit=8&status=active&search=${encodeURIComponent(prodSearch)}`)).data,
    enabled: assignOpen,
  });
  const { data: mpData, refetch: refetchMp } = useQuery({
    queryKey: ["agent-mp", id],
    queryFn: async () => (await api.get(`/admin/agents/${id}/marketplace`)).data,
  });
  const [credsFor, setCredsFor] = useState(null);
  const [credsForm, setCredsForm] = useState({});
  const { data: planList } = useQuery({ queryKey: ["plans-list"], queryFn: async () => (await api.get("/plans")).data });
  const assignPlan = useMutation({
    mutationFn: async (planId) => (await api.put(`/admin/agents/${id}/plan`, { plan_id: planId })).data,
    onSuccess: (d) => { toast.success(`Plan assigned: ${d.plan} · Invoice ${d.invoice_no}`); refetch(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const { data: kycData, refetch: refetchKyc } = useQuery({
    queryKey: ["agent-kyc", id],
    queryFn: async () => (await api.get(`/admin/agents/${id}/kyc`)).data,
  });
  const setKycStatus = useMutation({
    mutationFn: async (status) => (await api.post(`/admin/agents/${id}/kyc/status`, { status })).data,
    onSuccess: (d) => { toast.success(`KYC ${d.status}`); refetchKyc(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const saveCreds = useMutation({
    mutationFn: async () => (await api.put(`/admin/agents/${id}/marketplace-credentials/${credsFor}`, { credentials: credsForm })).data,
    onSuccess: (d) => { toast.success(d.configured ? "Credentials saved — ready to connect" : "Credentials saved"); setCredsFor(null); refetchMp(); refetch(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const openCreds = (ch) => {
    setCredsFor(ch);
    setCredsForm(agent.mp_credentials?.[ch] || {});
  };

  const assign = useMutation({
    mutationFn: async (pid) => api.post("/admin/products/assign", { product_ids: [pid], agent_ids: [id] }),
    onSuccess: () => { toast.success("Product assigned"); qc.invalidateQueries({ queryKey: ["agent-products", id] }); refetch(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const unassign = useMutation({
    mutationFn: async (pid) => api.post("/admin/products/unassign", { product_ids: [pid], agent_ids: [id] }),
    onSuccess: () => { toast.success("Product removed"); qc.invalidateQueries({ queryKey: ["agent-products", id] }); refetch(); },
  });
  const resetPassword = useMutation({
    mutationFn: async () => (await api.post(`/admin/agents/${id}/reset-password`, {})).data,
    onSuccess: (d) => setResetPw(d.password),
    onError: (e) => toast.error(errMsg(e)),
  });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const openEdit = () => {
    setEditForm({
      name: agent.name || "", business_name: agent.business_name || "", mobile: agent.mobile || "",
      address: agent.address || "", city: agent.city || "", state: agent.state || "", pincode: agent.pincode || "",
      gstin: agent.gstin || "", pan: agent.pan || "",
      bank_name: agent.bank?.bank || "", bank_account: agent.bank?.account || "", bank_ifsc: agent.bank?.ifsc || "",
      status: agent.status || "active",
    });
    setEditOpen(true);
  };
  const updateAgent = useMutation({
    mutationFn: async () => api.put(`/admin/agents/${id}`, {
      name: editForm.name, business_name: editForm.business_name, mobile: editForm.mobile,
      address: editForm.address, city: editForm.city, state: editForm.state, pincode: editForm.pincode,
      gstin: editForm.gstin, pan: editForm.pan, status: editForm.status,
      bank: { bank: editForm.bank_name, account: editForm.bank_account, ifsc: editForm.bank_ifsc },
    }),
    onSuccess: () => { toast.success("Agent updated"); setEditOpen(false); refetch(); qc.invalidateQueries({ queryKey: ["agents"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const connectMp = useMutation({
    mutationFn: async (channel) => (await api.post(`/admin/marketplace/${channel}/connect`, { agent_id: id })).data,
    onSuccess: (d) => { window.location.href = d.authorization_url; },
    onError: (e) => toast.error(errMsg(e)),
  });
  const syncOrders = useMutation({
    mutationFn: async (channel) => (await api.post("/admin/marketplace/sync-now", { agent_id: id, marketplace: channel })).data,
    onSuccess: (d, ch) => { d.status === "SYNCED" ? toast.success(`${ch}: sync complete`) : toast.warning(d.message || `${ch}: sync failed`); refetchMp(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const disconnectMp = useMutation({
    mutationFn: async (channel) => api.post("/admin/marketplace/disconnect", { agent_id: id, channel }),
    onSuccess: () => { toast.success("Disconnected"); refetch(); refetchMp(); },
  });

  if (!agent) return <p className="text-sm text-slate-500">Loading…</p>;
  const mpFor = (ch) => (mpData?.accounts || []).find((m) => m.channel === ch);
  const cfgFor = (ch) => mpData?.config?.[ch];

  return (
    <div data-testid="agent-detail-page">
      <PageHeader title={agent.business_name} sub={`${agent.name} · ${agent.email} · ${agent.mobile}`} testid="agent-detail-title">
        <StatusBadge status={agent.status} />
        <Button data-testid="edit-agent-btn" variant="outline" onClick={openEdit}
          className="border-slate-700 text-slate-300 hover:bg-slate-800"><Pencil size={13} /> Edit agent</Button>
        <Button data-testid="reset-agent-password-btn" variant="outline" onClick={() => resetPassword.mutate()}
          className="border-slate-700 text-slate-300 hover:bg-slate-800"><KeyRound size={13} /> Reset password</Button>
      </PageHeader>
      {resetPw && (
        <p data-testid="reset-password-result" className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-400">
          New password: <span className="font-mono">{resetPw}</span> — share securely with the agent.
        </p>
      )}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[["Products", agent.stats.products], ["Orders", agent.stats.orders], ["Sales", fmtINR(agent.stats.sales)], ["Agent Profit", fmtINR(agent.stats.profit)]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-800 bg-[#111827] p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
            <p className="mt-1 text-xl font-bold text-slate-50">{v}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="products" data-testid="agent-detail-tabs">
        <TabsList className="border border-slate-800 bg-[#111827]">
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="marketplace" data-testid="tab-marketplace">Marketplace Accounts</TabsTrigger>
          <TabsTrigger value="store" data-testid="tab-store">Store</TabsTrigger>
          <TabsTrigger value="plan" data-testid="tab-plan">Plan</TabsTrigger>
          <TabsTrigger value="kyc" data-testid="tab-kyc">KYC</TabsTrigger>
          <TabsTrigger value="info" data-testid="tab-info">Details</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-4">
          <div className="mb-3 flex gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input data-testid="assigned-search" value={prodSearch} onChange={(e) => { setProdSearch(e.target.value); setProdPage(1); }}
                placeholder="Search assigned…" className="border-slate-700 bg-[#111827] pl-8 text-xs text-slate-100" />
            </div>
            <Button data-testid="assign-more-btn" size="sm" onClick={() => setAssignOpen(true)} className="bg-orange-500 text-white hover:bg-orange-600">
              <UserPlus size={13} /> Assign products
            </Button>
          </div>
          {!assigned?.items?.length ? <EmptyState title="No products assigned" /> : (
            <div className="space-y-2">
              {assigned.items.map((p) => (
                <div key={p.id} data-testid={`assigned-product-${p.sku}`} className="flex items-center justify-between rounded-lg border border-slate-800 bg-[#111827] px-3 py-2.5">
                  <div className="flex items-center gap-3">
                    {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="h-9 w-9 rounded-lg object-cover" />}
                    <div>
                      <p className="text-xs font-medium text-slate-200">{p.name}</p>
                      <p className="font-mono text-[10px] text-slate-500">{p.sku} · store {fmtINR(p.assignment?.price_store)}</p>
                    </div>
                  </div>
                  <Button data-testid={`unassign-${p.sku}`} size="sm" variant="outline" onClick={() => unassign.mutate(p.id)}
                    className="border-slate-700 text-[10px] text-rose-400 hover:bg-rose-500/10">Remove</Button>
                </div>
              ))}
            </div>
          )}
          {assigned && <Pager page={assigned.page} pages={assigned.pages} onPage={setProdPage} />}
        </TabsContent>

        <TabsContent value="marketplace" className="mt-4">
          <p className="mb-4 flex items-center gap-2 text-[11px] text-slate-500">
            <KeyRound size={12} /> Seller authorization happens on Amazon/Flipkart. Credentials and tokens stay server-side — agents never see them.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {["amazon", "flipkart"].map((ch) => {
              const acc = mpFor(ch);
              const cfg = cfgFor(ch);
              const connected = acc?.status === "connected";
              return (
                <div key={ch} data-testid={`mp-${ch}-card`} className="rounded-xl border border-slate-800 bg-[#111827] p-5">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Plug size={15} className="text-orange-400" />
                      <h3 className="text-sm font-semibold capitalize text-slate-100">{ch}</h3>
                    </div>
                    {connected && acc?.mode === "demo" ? <DemoBadge /> : acc?.mode === "sandbox" && connected ? (
                      <span data-testid={`mp-${ch}-sandbox`} className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-300">Sandbox</span>
                    ) : connected ? (
                      <span data-testid={`mp-${ch}-live`} className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-400">Live</span>
                    ) : null}
                  </div>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <Button data-testid={`mp-creds-${ch}`} size="sm" variant="outline" onClick={() => openCreds(ch)}
                      className="border-slate-700 text-[10px] text-slate-300 hover:bg-slate-800">
                      <KeyRound size={11} /> {cfg?.agent_credentials ? "Edit agent credentials" : "Add agent credentials"}
                    </Button>
                    {cfg?.configured && (
                      <span data-testid={`mp-${ch}-source`} className="rounded-full border border-slate-600 px-2 py-0.5 text-[9px] font-semibold uppercase text-slate-400">
                        {cfg.source === "agent" ? "Agent credentials" : "Platform credentials"}
                      </span>
                    )}
                  </div>

                  {connected ? (
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 size={14} /> Connected</div>
                      <div className="flex justify-between"><span className="text-slate-500">Seller ID</span><span className="font-mono text-slate-300">{acc.seller_id || "—"}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Listings</span><span className="text-slate-300">{acc.listings ?? 0}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Last sync</span><span className="text-slate-400">{acc.last_sync ? new Date(acc.last_sync).toLocaleString("en-IN") : "Never"}</span></div>
                      {acc.error && <p className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-amber-400">{acc.error}</p>}
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button data-testid={`mp-sync-orders-${ch}`} size="sm" onClick={() => syncOrders.mutate(ch)} disabled={syncOrders.isPending}
                          className="bg-orange-500 text-white hover:bg-orange-600">
                          <Download size={12} /> {syncOrders.isPending ? "Syncing…" : "Sync orders"}
                        </Button>
                        <Button data-testid={`mp-reconnect-${ch}`} size="sm" variant="outline" onClick={() => connectMp.mutate(ch)}
                          disabled={!cfg?.configured} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                          <Link2 size={12} /> Reconnect
                        </Button>
                        <Button data-testid={`mp-disconnect-${ch}`} size="sm" variant="outline" onClick={() => disconnectMp.mutate(ch)}
                          className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10">Disconnect</Button>
                      </div>
                    </div>
                  ) : cfg?.configured ? (
                    <div className="space-y-3">
                      {acc?.error && !(acc.error === "Disconnected by admin" && cfg?.configured) && <p className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-400">{acc.error}</p>}
                      <p className="text-xs text-slate-500">
                        App credentials configured{ch === "amazon" && cfg.mode === "sandbox" ? " (sandbox mode)" : ""}. Click below — the seller authorizes on {ch === "amazon" ? "Amazon Seller Central" : "Flipkart"}.
                      </p>
                      <Button data-testid={`mp-connect-${ch}`} size="sm" onClick={() => connectMp.mutate(ch)} disabled={connectMp.isPending}
                        className="bg-orange-500 font-semibold text-white hover:bg-orange-600">
                        <Link2 size={13} /> {connectMp.isPending ? "Opening…" : `Connect with ${ch === "amazon" ? "Amazon" : "Flipkart"}`}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="flex items-start gap-2 text-xs text-amber-400">
                        <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                        {ch === "amazon" ? "Amazon SP-API credentials required for this agent (Application ID, LWA client, AWS IAM keys)."
                          : "Flipkart credentials required for this agent (Client ID + Client Secret)."}
                        Add them with the Credentials button above — platform defaults are used as fallback.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="store" className="mt-4">
          {agent.store ? (
            <div className="rounded-xl border border-slate-800 bg-[#111827] p-5 text-xs space-y-2" data-testid="agent-store-info">
              <div className="flex justify-between"><span className="text-slate-500">Store</span><span className="font-medium text-slate-200">{agent.store.name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Status</span><StatusBadge status={agent.store.status} /></div>
              <div className="flex justify-between"><span className="text-slate-500">Theme</span><span className="capitalize text-slate-300">{agent.store.theme?.replace(/_/g, " ")}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">URL</span>
                <a data-testid="agent-store-link" href={`/store/${agent.store.slug}`} target="_blank" rel="noreferrer" className="text-orange-400 hover:text-orange-300">/store/{agent.store.slug}</a></div>
              <div className="flex justify-between"><span className="text-slate-500">Custom domain</span><span className="text-slate-300">{agent.store.custom_domain || "—"}</span></div>
            </div>
          ) : <EmptyState title="No store yet" sub="Agent has not completed one-click store setup." />}
        </TabsContent>

        <TabsContent value="plan" className="mt-4">
          <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="agent-plan-card">
            <h3 className="mb-3 text-sm font-semibold text-slate-200">Current Plan</h3>
            {agent.plan_name ? (
              <div className="grid gap-2 text-xs sm:grid-cols-2">
                <div className="flex justify-between"><span className="text-slate-500">Plan</span><span className="font-semibold text-orange-400">{agent.plan_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Activated</span><span className="text-slate-300">{agent.plan_activated_at ? new Date(agent.plan_activated_at).toLocaleDateString("en-IN") : "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Service free until</span><span className="text-slate-300">{agent.service_free_until || "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Renewal due</span><span className="text-slate-300">{agent.service_renewal_due || "—"}</span></div>
              </div>
            ) : <p className="text-xs text-slate-500">No plan assigned — the agent cannot add products until a plan is active.</p>}
          </div>
          <h3 className="mb-3 mt-5 text-sm font-semibold text-slate-200">Assign / Change Plan</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            {(planList || []).map((p) => (
              <div key={p.id} data-testid={`assign-plan-${p.slug}`} className="rounded-xl border border-slate-800 bg-[#111827] p-4">
                <p className="text-xs font-bold uppercase tracking-widest text-blue-400">{p.name}</p>
                <p className="mt-1 text-xl font-bold text-slate-50">{fmtINR(p.price)}</p>
                <p className="text-[10px] text-slate-500">{p.max_products} products · one-time</p>
                <Button size="sm" data-testid={`assign-plan-btn-${p.slug}`} onClick={() => assignPlan.mutate(p.id)} disabled={assignPlan.isPending}
                  className="mt-3 w-full bg-orange-500 text-[11px] font-semibold text-white hover:bg-orange-600">
                  {agent.plan_id === p.id ? "Re-assign" : "Assign"} (manual)
                </Button>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[10px] text-slate-500">Manual assignment creates a paid invoice marked "manual" — use when payment was collected offline.</p>
        </TabsContent>

        <TabsContent value="kyc" className="mt-4">
          {(() => {
            const kyc = kycData?.kyc || {};
            const bank = kyc.bank || {};
            const docs = kyc.documents || {};
            return (
              <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="agent-kyc-card">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-200">KYC & Bank — {kycData?.business_name}</h3>
                  <StatusBadge status={kyc.status || "incomplete"} />
                </div>
                <div className="grid gap-2 text-xs sm:grid-cols-2">
                  {[["Account holder", bank.holder], ["Account number", bank.account], ["IFSC", bank.ifsc],
                    ["Bank", bank.bank_name], ["PAN", kyc.pan], ["GSTIN", kyc.gstin], ["Address", kyc.address]].map(([l, v]) => (
                    <div key={l} className="flex justify-between gap-3"><span className="text-slate-500">{l}</span><span className="text-right font-mono text-slate-300">{v || "—"}</span></div>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap gap-3 border-t border-slate-800 pt-4 text-xs">
                  {[["cheque", "Cancelled cheque"], ["gst_certificate", "GST certificate"], ["pan_card", "PAN card"]].map(([k, label]) => (
                    docs[k]
                      ? <a key={k} data-testid={`kyc-doc-${k}`} href={imgUrl(docs[k])} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">{label} ↗</a>
                      : <span key={k} className="text-slate-600">{label}: not uploaded</span>
                  ))}
                </div>
                {kyc.status === "submitted" && (
                  <div className="mt-4 flex gap-2">
                    <Button data-testid="kyc-verify-btn" size="sm" onClick={() => setKycStatus.mutate("verified")} disabled={setKycStatus.isPending}
                      className="bg-emerald-500 text-xs font-bold text-white hover:bg-emerald-600">Verify KYC</Button>
                    <Button data-testid="kyc-reject-btn" size="sm" variant="outline" onClick={() => setKycStatus.mutate("rejected")} disabled={setKycStatus.isPending}
                      className="border-rose-500/40 text-xs text-rose-400 hover:bg-rose-500/10">Reject</Button>
                  </div>
                )}
                {kyc.status === "verified" && <p className="mt-4 text-[11px] text-emerald-400">Verified — this agent can withdraw payouts.</p>}
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="info" className="mt-4">
          <div className="grid gap-4 rounded-xl border border-slate-800 bg-[#111827] p-5 text-xs sm:grid-cols-2" data-testid="agent-info">
            {[["GSTIN", agent.gstin], ["PAN", agent.pan], ["Address", agent.address], ["City", agent.city], ["State", agent.state],
              ["Pincode", agent.pincode], ["Bank", agent.bank?.bank ? `${agent.bank.bank} · ${agent.bank.account}` : "—"],
              ["Ledger balance", fmtINR(agent.ledger_balance)], ["Joined", new Date(agent.created_at).toLocaleDateString("en-IN")]].map(([l, v]) => (
              <div key={l}><p className="text-slate-500">{l}</p><p className="mt-0.5 text-slate-200">{v || "—"}</p></div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader><DialogTitle>Edit Agent — {agent.business_name}</DialogTitle></DialogHeader>
          {editForm && (
            <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                {[["name", "Full name"], ["business_name", "Business name"], ["mobile", "Mobile"], ["city", "City"],
                  ["state", "State"], ["pincode", "Pincode"], ["gstin", "GSTIN"], ["pan", "PAN"]].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs text-slate-400">{label}</Label>
                    <Input data-testid={`edit-agent-${k}`} value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })}
                      className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                  </div>
                ))}
              </div>
              <div>
                <Label className="text-xs text-slate-400">Address</Label>
                <Input data-testid="edit-agent-address" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[["bank_name", "Bank"], ["bank_account", "Account No"], ["bank_ifsc", "IFSC"]].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs text-slate-400">{label}</Label>
                    <Input data-testid={`edit-agent-${k}`} value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })}
                      className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                  </div>
                ))}
              </div>
              <div>
                <Label className="text-xs text-slate-400">Status</Label>
                <div className="mt-1.5 flex gap-2">
                  {["active", "inactive"].map((st) => (
                    <button key={st} data-testid={`edit-agent-status-${st}`} onClick={() => setEditForm({ ...editForm, status: st })}
                      className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium capitalize transition-colors ${editForm.status === st ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-slate-700 text-slate-400"}`}>
                      {st}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-[10px] text-slate-500">Login email is fixed. Use "Reset password" for credential changes. Marketplace accounts are connected via the Marketplace tab.</p>
              <Button data-testid="edit-agent-save" onClick={() => updateAgent.mutate()} disabled={updateAgent.isPending}
                className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600">
                {updateAgent.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!credsFor} onOpenChange={() => setCredsFor(null)}>
        <DialogContent className="max-w-lg border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader><DialogTitle className="capitalize">{credsFor} credentials — {agent.business_name}</DialogTitle></DialogHeader>
          <p className="text-[11px] text-slate-500">This agent's own {credsFor} app credentials. Stored encrypted, server-side only — the agent never sees them.</p>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {(credsFor === "amazon"
              ? [["application_id", "Application ID"], ["client_id", "LWA Client ID"], ["client_secret", "LWA Client Secret"],
                 ["aws_access_key", "AWS Access Key ID"], ["aws_secret_key", "AWS Secret Access Key"], ["sandbox_token", "Sandbox Token (optional)"]]
              : [["client_id", "Client ID (appId)"], ["client_secret", "Client Secret (appSecret)"]]
            ).map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs text-slate-400">{label}</Label>
                <Input data-testid={`creds-${k}`} value={credsForm[k] || ""} onChange={(e) => setCredsForm({ ...credsForm, [k]: e.target.value })}
                  placeholder={k.includes("secret") || k.includes("token") ? "••••••••" : ""}
                  className="mt-1 border-slate-700 bg-slate-900 font-mono text-xs text-slate-100" />
              </div>
            ))}
            {credsFor === "amazon" && (
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-400">Mode:</Label>
                {["sandbox", "live"].map((m) => (
                  <button key={m} data-testid={`creds-mode-${m}`}
                    onClick={() => setCredsForm({ ...credsForm, sandbox: m === "sandbox" })}
                    className={`rounded-lg border px-3 py-1 text-xs font-medium capitalize transition-colors ${(credsForm.sandbox ?? true) === (m === "sandbox") ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-slate-700 text-slate-400"}`}>
                    {m}
                  </button>
                ))}
              </div>
            )}
            <Button data-testid="creds-save-btn" onClick={() => saveCreds.mutate()} disabled={saveCreds.isPending}
              className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600">
              {saveCreds.isPending ? "Saving…" : "Save credentials"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader><DialogTitle>Assign products to {agent.business_name}</DialogTitle></DialogHeader>
          <Input data-testid="picker-search" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} placeholder="Search catalog…"
            className="border-slate-700 bg-slate-900 text-slate-100" />
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {(catalog?.items || []).map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
                <div>
                  <p className="text-xs font-medium text-slate-200">{p.name}</p>
                  <p className="font-mono text-[10px] text-slate-500">{p.sku} · {fmtINR(p.suggested_price)}</p>
                </div>
                <Button data-testid={`picker-assign-${p.sku}`} size="sm" variant="outline" onClick={() => assign.mutate(p.id)}
                  className="border-orange-500/40 text-orange-400 hover:bg-orange-500/10">Assign</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
